<script setup>
import { computed, provide, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';
import fromUnixTime from 'date-fns/fromUnixTime';
import { useMapGetter } from 'dashboard/composables/store';
import { downloadXlsxFile } from 'dashboard/helper/downloadHelper';
import V4Button from 'dashboard/components-next/button/Button.vue';
import ReportHeader from './components/ReportHeader.vue';
import ReportFilterSelector from './components/FilterSelector.vue';
import ContactRankingMode from './components/Filters/ContactRankingMode.vue';
import { buildContactRankingRows } from './helpers/contactRankingCsv';

const { t } = useI18n();

const route = useRoute();
const router = useRouter();
const rankingMode = ref('companies');
const filters = ref({
  from: 0,
  to: 0,
  businessHours: false,
  inboxId: undefined,
  teamId: undefined,
  agentIds: undefined,
  ranking: 'companies',
});

provide('contactReportFilters', filters);

const report = useMapGetter('summaryReports/getContactSummaryReports');
const uiFlags = useMapGetter('summaryReports/getUIFlags');
const isIndex = computed(() => route.name === 'contact_reports_index');
const isCompanyRanking = computed(() => rankingMode.value !== 'contacts');
const rankingRows = computed(() => {
  const data = report.value || {};
  return isCompanyRanking.value ? data.companies || [] : data.contacts || [];
});
const canDownload = computed(
  () =>
    rankingRows.value.length > 0 &&
    !uiFlags.value.isFetchingContactSummaryReports
);

const agentIdsFrom = updatedFilter => {
  if (!updatedFilter) return filters.value.agentIds;
  if (!updatedFilter.selectedAgents?.length) return undefined;
  return updatedFilter.selectedAgents.map(agent => agent.id);
};

const applyFilters = updatedFilter => {
  filters.value = {
    from: updatedFilter?.from ?? filters.value.from,
    to: updatedFilter?.to ?? filters.value.to,
    businessHours: updatedFilter?.businessHours ?? filters.value.businessHours,
    inboxId: updatedFilter
      ? updatedFilter.selectedInbox?.id
      : filters.value.inboxId,
    teamId: updatedFilter
      ? updatedFilter.selectedTeam?.id
      : filters.value.teamId,
    agentIds: agentIdsFrom(updatedFilter),
    ranking: rankingMode.value,
  };
};

const onFilterChange = updatedFilter => {
  applyFilters(updatedFilter);
};

const spreadsheetLabels = () => ({
  operationTitle: t('CONTACT_REPORTS.OPERATION.TITLE'),
  companies: t('CONTACT_REPORTS.METRICS.COMPANIES'),
  contacts: t('CONTACT_REPORTS.TABLE.CONTACTS'),
  engagements: t('CONTACT_REPORTS.METRICS.ENGAGEMENTS'),
  messages: t('CONTACT_REPORTS.METRICS.MESSAGES'),
  resolved: t('CONTACT_REPORTS.METRICS.RESOLVED'),
  avgHandling: t('CONTACT_REPORTS.TABLE.AVG_HANDLING_TIME'),
  firstResponse: t('CONTACT_REPORTS.METRICS.FIRST_RESPONSE'),
  replyTime: t('CONTACT_REPORTS.METRICS.REPLY_TIME'),
  rank: t('CONTACT_REPORTS.TABLE.RANK'),
  company: t('CONTACT_REPORTS.TABLE.COMPANY'),
  domain: t('CONTACT_REPORTS.TABLE.DOMAIN'),
  contact: t('CONTACT_REPORTS.TABLE.CONTACT'),
  email: t('CONTACT_REPORTS.TABLE.EMAIL'),
  phone: t('CONTACT_REPORTS.TABLE.PHONE'),
  totalHandling: t('CONTACT_REPORTS.TABLE.TOTAL_HANDLING_TIME'),
});

const downloadSpreadsheet = () => {
  if (!canDownload.value) return;

  const periodLabel =
    filters.value.from && filters.value.to
      ? t('CONTACT_REPORTS.PERIOD', {
          from: format(fromUnixTime(filters.value.from), 'dd/MM/yyyy'),
          to: format(fromUnixTime(filters.value.to), 'dd/MM/yyyy'),
        })
      : '';
  const stamp = filters.value.to
    ? format(fromUnixTime(filters.value.to), 'dd-MM-yyyy')
    : format(new Date(), 'dd-MM-yyyy');
  const slug = isCompanyRanking.value ? 'empresas' : 'contatos-soltos';
  const sheetRows = buildContactRankingRows({
    ranking: rankingMode.value,
    rows: rankingRows.value,
    operation: report.value?.operation || {},
    periodLabel,
    labels: spreadsheetLabels(),
  });

  downloadXlsxFile(`ranking-clientes-${slug}-${stamp}.xlsx`, sheetRows);
};

watch(rankingMode, () => {
  applyFilters();
  if (route.name === 'contact_reports_show') {
    router.push({ name: 'contact_reports_index' });
  }
});
</script>

<template>
  <ReportHeader
    v-if="isIndex"
    :header-title="$t('CONTACT_REPORTS.HEADER')"
    :header-description="$t('CONTACT_REPORTS.DESCRIPTION')"
  >
    <V4Button
      :label="$t('CONTACT_REPORTS.DOWNLOAD')"
      icon="i-ph-download-simple"
      size="sm"
      :disabled="!canDownload"
      @click="downloadSpreadsheet"
    />
  </ReportHeader>

  <ReportFilterSelector
    show-agents-filter
    show-inbox-filter
    show-team-filter
    @filter-change="onFilterChange"
  >
    <template #leading>
      <ContactRankingMode v-model="rankingMode" />
    </template>
  </ReportFilterSelector>

  <router-view />
</template>
